// Data hooks for the Chat + Incidents surface. An incident IS a workflow run,
// so these read the same endpoints the triage board + run-detail already use
// (`v1/runs`, `v1/runs/:id`, `v1/runs/:id/node-executions`, `v1/workflows`) and
// reuse their react-query keys so caches are shared, not duplicated.
import { useClient } from '@cortex/react-query';
import type { NodeExecution, Run } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { terminalRunStatuses, type RunStatusKey } from '../../../lib/constants';

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

/** A run joined with its workflow name — the row model for the incidents rail. */
export interface IIncidentListItem {
  run: Run;
  workflowName: string | null;
}

/** Minimal workflow shape read from `v1/workflows` just for the name join. */
interface IWorkflowName {
  id: string;
  name: string;
}

function unwrap<T>(raw: IListEnvelope<T> | T[] | undefined): T[] {
  if (!raw) return [];
  return Array.isArray(raw) ? raw : raw.items;
}

/**
 * List recent runs as incidents, joined to their workflow names and sorted
 * most-recent first. Polls on a slow interval so the rail stays fresh while a
 * run is in flight; the join query is cached longer since workflow names rarely
 * change.
 */
export function useIncidentRuns(): {
  items: IIncidentListItem[];
  isLoading: boolean;
  error: unknown;
} {
  const client = useClient();

  const runs = useQuery({
    queryKey: ['incidents', 'runs'],
    queryFn: () => client.http.get('v1/runs?limit=200').json<IListEnvelope<Run> | Run[]>(),
    refetchInterval: 15_000,
  });

  const workflows = useQuery({
    queryKey: ['incidents', 'workflow-names'],
    queryFn: () =>
      client.http
        .get('v1/workflows?limit=200')
        .json<IListEnvelope<IWorkflowName> | IWorkflowName[]>(),
    staleTime: 60_000,
  });

  const items = useMemo<IIncidentListItem[]>(() => {
    const runList = unwrap(runs.data);
    const nameById = new Map<string, string>();
    for (const wf of unwrap(workflows.data)) nameById.set(wf.id, wf.name);
    return [...runList]
      .sort((a, b) => sortKey(b) - sortKey(a))
      .map((run) => ({ run, workflowName: nameById.get(run.workflowId) ?? null }));
  }, [runs.data, workflows.data]);

  return { items, isLoading: runs.isLoading, error: runs.error };
}

/** Most-recent activity timestamp for ordering (startedAt, else createdAt). */
function sortKey(run: Run): number {
  const started = run.startedAt ? new Date(run.startedAt).getTime() : 0;
  if (started) return started;
  return run.createdAt ? new Date(run.createdAt).getTime() : 0;
}

/** A single run's detail — for the injected-incident card header. */
export function useIncidentRun(runId: string) {
  const client = useClient();
  return useQuery({
    queryKey: ['run', runId],
    queryFn: () => client.http.get(`v1/runs/${runId}`).json<Run>(),
    enabled: Boolean(runId),
  });
}

/**
 * A run's node executions — the nodes that ran, in order, with their traces.
 * Shares the `['run-node-executions', runId, 'trace']` key with
 * RunExecutionTrace so opening an incident in the rail warms the same cache the
 * run-detail surface uses.
 */
export function useIncidentNodeExecutions(runId: string) {
  const client = useClient();
  return useQuery({
    queryKey: ['run-node-executions', runId, 'trace'],
    queryFn: () =>
      client.http
        .get(`v1/runs/${runId}/node-executions?limit=200`)
        .json<IListEnvelope<NodeExecution> | NodeExecution[]>(),
    enabled: Boolean(runId),
    select: (raw) => unwrap(raw),
  });
}

/** True while a run is still active (worth polling / "Working on it"). */
export function isActiveRun(status: RunStatusKey): boolean {
  return !terminalRunStatuses.has(status);
}
